import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { ComponentLike } from '@glint/template';

import type { PopupInnerComponentClass, Service } from 'webapp-types';
import { PopupEvent, PopupState } from 'webapp/enums/enums';

import PopupWrapperContextComponent from 'webapp/components/popup/wrapper/context';
import PopupWrapperModalComponent from 'webapp/components/popup/wrapper/modal';

import Serviced from 'webapp/models-custom/serviced';
import { themeClassNames } from 'webapp/models-custom/theme';

// This is a base popup model

type EventName = PopupEvent | string;
type EventContext = unknown;
type EventAction = (...args: unknown[]) => unknown; // any function with any params
interface EventCollection {
	eventName: EventName;
	context: EventContext;
	fn: EventAction;
}

// The wrapper and inner components are resolved dynamically at runtime; the runtime guarantees
// each popup is paired with a wrapper/inner that accepts it. We expose them as a ComponentLike
// keyed on the base model so `(component ...)` type-checks the `@model={{popup}}` invocation.
type PopupComponent = ComponentLike<{
	Args: { model: PopupBase };
	Blocks: { default: [] };
}>;

export interface PopupProps {
	component: PopupInnerComponentClass;
	// componentName?: string;
	allowStacked?: boolean;
	allowMultipleInstances?: boolean;
	isAnimated?: boolean;
	autofocus?: boolean;
	hasOverlay?: boolean;
	closeOnOverlayClick?: boolean;
	popupClass?: string;
	theme?: string;
}

export default abstract class PopupBase extends Serviced {
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;

	componentInnerClass: PopupInnerComponentClass; // component class prototype reference
	component: PopupWrapperModalComponent | PopupWrapperContextComponent | null = null; // actual wrapper class instance

	allowStacked = false; // allow other popups to open on top of this one
	allowMultipleInstances = false; // allow multiple instances of the same. also needs allowStacked: true then.
	isAnimated = true; // to animate popup or not
	hasOverlay = false;
	closeOnOverlayClick = false;
	autofocus = true;

	popupClass = '';
	private _theme = '';

	private events: EventCollection[] = [];

	// user-loaded data (FROZEN on passing - first level only)
	@tracked data: object = {};

	@tracked _currentState: number = PopupState.INACTIVE;

	statePromise: Promise<unknown> | null = null;

	constructor(props: PopupProps) {
		super();

		this.componentInnerClass = props.component;

		// if (props.componentName) {
		// 	this.componentName = props.componentName;
		// }

		this.allowMultipleInstances = !!props.allowMultipleInstances;
		this.allowStacked = !!props.allowStacked || this.allowMultipleInstances;

		if (typeof props.popupClass !== 'undefined') {
			this.popupClass = props.popupClass;
		}

		if (typeof props.hasOverlay !== 'undefined') {
			this.hasOverlay = props.hasOverlay;
		}

		if (typeof props.closeOnOverlayClick !== 'undefined') {
			this.closeOnOverlayClick = props.closeOnOverlayClick;
		}

		if (typeof props.isAnimated !== 'undefined') {
			this.isAnimated = props.isAnimated;
		}

		if (typeof props.autofocus !== 'undefined') {
			this.autofocus = props.autofocus;
		}

		if (typeof props.theme !== 'undefined') {
			this._theme = props.theme;
		}
	}

	get isModal(): boolean {
		return false;
	}

	get isContext(): boolean {
		return false;
	}

	// Drawers render their own close, so they're dismissable without `showClose` / `closeOnOverlayClick`
	get isDrawer(): boolean {
		return this.popupClass.includes('popup-type-drawer');
	}

	get wrapperComponent(): PopupComponent {
		return (this.isModal
			? PopupWrapperModalComponent
			: PopupWrapperContextComponent) as unknown as PopupComponent;
	}

	get innerComponent(): PopupComponent {
		return this.componentInnerClass as unknown as PopupComponent;
	}

	get theme(): string | undefined {
		return this._theme || undefined;
	}

	get themeClass(): string {
		return themeClassNames(this.theme);
	}

	get state(): number {
		return this._currentState || PopupState.INACTIVE;
	}

	set state(state: number) {
		const currentState = this._currentState;

		if (currentState !== state) {
			let event;

			if (currentState === PopupState.INACTIVE && state === PopupState.OPENING) {
				event = PopupEvent.OPENING;
			} else if (currentState === PopupState.OPENING && state === PopupState.OPEN) {
				event = PopupEvent.OPENED;
			} else if (currentState === PopupState.OPEN && state === PopupState.CLOSING) {
				event = PopupEvent.CLOSING;
			} else if (currentState === PopupState.CLOSING && state === PopupState.INACTIVE) {
				event = PopupEvent.CLOSED;
			}
			// console.log('popup state change', this.componentName, event, {currentState, state});

			this._currentState = state;

			if (event) {
				this.triggerEvent(event);
			}
		}
	}

	get isInactive(): boolean {
		return this.state === PopupState.INACTIVE;
	}

	get isOpening(): boolean {
		return this.state === PopupState.OPENING;
	}

	get isOpen(): boolean {
		return this.state === PopupState.OPEN;
	}

	get isClosing(): boolean {
		return this.state === PopupState.CLOSING;
	}

	open(data?: object): PopupBase | void {
		return this.popupService.open(this, data || {});
	}

	on(eventName: EventName, context: EventContext, fn: EventAction): void {
		this.events.push({ eventName, context, fn });
	}

	off(eventName: EventName, context: EventContext, fn: EventAction): void {
		this.events = this.events.filter(
			(evt) => evt.eventName !== eventName || evt.context !== context || evt.fn !== fn,
		);
	}

	offAll(eventName: EventName): void {
		this.events = this.events.filter((evt) => evt.eventName !== eventName);
	}

	triggerEvent(eventName: EventName, ...eventArgs: unknown[]): unknown[] {
		const events = this.events.filter((item) => item.eventName === eventName);
		const eventResponses = events.map((evt) => {
			return evt.fn.bind(evt.context)(...eventArgs);
		});
		return eventResponses;
	}

	readyToClose(): Promise<void> {
		const component = this.component;
		if (!component) {
			return Promise.resolve();
		}
		return component.readyToClose();
	}

	reposition(): void {
		const component = this.component;
		if (!component) {
			return;
		}
		component.reposition();
	}

	close(_reason = ''): Promise<unknown> {
		// console.log('popup closing', this.componentInnerClass, { reason: _reason });

		// modals can block closing ("leave-confirm" functionality)
		// before closing, PopupEvent.CLOSE_INTENT is triggered
		// if it returns true, we block closing
		const eventResponses = this.triggerEvent(PopupEvent.CLOSE_INTENT);
		if (eventResponses.some((response) => !!response)) {
			return Promise.resolve();
		}

		return this.popupService.close(this);
	}

	cleanup(): void {
		this.events = [];
	}
}
